import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { positionDentalPicker } from "./dentalPickerPosition";
import {
  Building2,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock3,
  LoaderCircle,
  Lock,
  Phone,
  Plus,
  Search,
  Stethoscope,
  UserRound,
  X,
} from "lucide-react";

type PickerKey = "date" | "start" | "end" | "duration" | "room" | "patient" | "doctor" | "service";
type AutoDateStage = "idle" | "requested" | "checking" | "unavailable" | "preference" | "searching" | "matched";
type AutoTimeStage = "idle" | "requested" | "unavailable" | "selecting" | "matched";
type AutoWorkflowStage = "idle" | "requested-date" | "requested-time" | "checking" | "unavailable" | "preference" | "searching" | "matched-date" | "matched-time" | "matched-complete";
type AutoPatientStage = "idle" | "opening" | "first-name" | "last-name" | "mobile" | "ready" | "saving" | "confirmed" | "saved";
type AutoVisitStage = "idle" | "ready" | "pressing" | "submitting" | "submitted";

type VisitDate = {
  year: number;
  month: number;
  day: number;
  label: string;
  weekday: string;
};

type VisitDraft = {
  date: VisitDate | null;
  start: string | null;
  end: string | null;
  duration: number | null;
  room: string;
  branch: string;
  patient: EntityOption | null;
  doctor: EntityOption | null;
  service: ServiceOption | null;
};

type EntityOption = {
  id: string;
  initials: string;
  title: string;
  subtitle: string;
  meta?: string;
};

type ServiceOption = EntityOption & {
  duration: number;
};

type CalendarCell = VisitDate & {
  inCurrentMonth: boolean;
};

const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const shortMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const weekdayHeadings = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const matchedDate: VisitDate = { year: 2026, month: 7, day: 27, label: "Aug 27", weekday: "Thursday" };
const requestedDate: VisitDate = { year: 2026, month: 7, day: 25, label: "Aug 25", weekday: "Tuesday" };

const patientOptions: EntityOption[] = [
  { id: "jordan", initials: "JL", title: "Jordan Lee", subtitle: "+1 202-555-0147", meta: "New patient" },
  { id: "alex", initials: "AM", title: "Alex Morgan", subtitle: "+1 202-555-0188", meta: "Existing patient" },
  { id: "taylor", initials: "TW", title: "Taylor Wong", subtitle: "+1 202-555-0122", meta: "Existing patient" },
];

const doctorOptions: EntityOption[] = [
  { id: "maya", initials: "MC", title: "Dr. Maya Chen", subtitle: "General dentist · North Office", meta: "Available at 4:45 PM" },
  { id: "daniel", initials: "DR", title: "Dr. Daniel Ross", subtitle: "Orthodontist · North Office", meta: "Next opening 5:15 PM" },
  { id: "sofia", initials: "SP", title: "Dr. Sofia Patel", subtitle: "Endodontist · Central Office", meta: "Not at this location" },
];

const automatedService: ServiceOption = { id: "cleaning", initials: "CL", title: "Cleaning", subtitle: "Routine hygiene visit", meta: "60 minutes", duration: 60 };

const serviceOptions: ServiceOption[] = [
  { id: "dental-exam", initials: "EX", title: "Dental examination", subtitle: "Routine oral examination", meta: "45 minutes", duration: 45 },
  automatedService,
  { id: "consultation", initials: "CO", title: "Consultation", subtitle: "General treatment consultation", meta: "30 minutes", duration: 30 },
  { id: "implantation", initials: "IM", title: "Implantation", subtitle: "Implant consultation", meta: "60 minutes", duration: 60 },
  { id: "rct", initials: "RC", title: "RCT", subtitle: "Root canal consultation", meta: "45 minutes", duration: 45 },
];

const roomOptions = [
  { id: "room-2", room: "Treatment Room 2", branch: "North Office", meta: "Available" },
  { id: "room-1", room: "Treatment Room 1", branch: "North Office", meta: "Available after 5:00 PM" },
  { id: "hygiene-1", room: "Hygiene 1", branch: "North Office", meta: "Cleaning only" },
];

const startTimeOptions = [
  { value: "4:00 PM", disabled: false },
  { value: "4:15 PM", disabled: false },
  { value: "4:30 PM", disabled: true },
  { value: "4:45 PM", disabled: false },
  { value: "5:00 PM", disabled: false },
  { value: "5:15 PM", disabled: false },
  { value: "5:30 PM", disabled: false },
  { value: "5:45 PM", disabled: false },
];

const endTimeOptions = ["4:45 PM", "5:00 PM", "5:15 PM", "5:30 PM", "5:45 PM", "6:00 PM", "6:15 PM", "6:30 PM"].map((value) => ({ value, disabled: false }));
const durationOptions = [30, 45, 60, 75].map((value) => ({ value: `${value} min`, disabled: false, minutes: value }));

function formatVisitDate(year: number, month: number, day: number): VisitDate {
  const weekday = weekdayNames[new Date(Date.UTC(year, month, day)).getUTCDay()];
  return { year, month, day, label: `${shortMonthNames[month]} ${day}`, weekday };
}

function buildCalendarCells(year: number, month: number): CalendarCell[] {
  const firstWeekday = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(Date.UTC(year, month, index - firstWeekday + 1));
    const cellYear = date.getUTCFullYear();
    const cellMonth = date.getUTCMonth();
    const cellDay = date.getUTCDate();
    return { ...formatVisitDate(cellYear, cellMonth, cellDay), inCurrentMonth: cellMonth === month };
  });
}

function timeToMinutes(value: string) {
  const match = value.match(/^(\d{1,2}):(\d{2})\s(AM|PM)$/);
  if (!match) return 0;
  let hours = Number(match[1]) % 12;
  if (match[3] === "PM") hours += 12;
  return hours * 60 + Number(match[2]);
}

function minutesToTime(minutes: number) {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hours24 = Math.floor(normalized / 60);
  const minute = normalized % 60;
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${String(minute).padStart(2, "0")} ${period}`;
}

function moveListboxFocus(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
  const options = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])'));
  if (!options.length) return;
  event.preventDefault();
  const currentIndex = options.indexOf(document.activeElement as HTMLElement);
  const direction = event.key === "ArrowDown" ? 1 : -1;
  const nextIndex = currentIndex < 0 ? 0 : (currentIndex + direction + options.length) % options.length;
  options[nextIndex]?.focus();
}

function FloatingPanel({
  open,
  anchor,
  onClose,
  width,
  ariaLabel,
  autoFocus = true,
  presentation = false,
  children,
}: {
  open: boolean;
  anchor: HTMLElement | null;
  onClose: () => void;
  width: number;
  ariaLabel: string;
  autoFocus?: boolean;
  presentation?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const panelId = useId();
  const [mounted, setMounted] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 0, panelWidth: width, placement: "bottom", ready: false });

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!mounted || !open || !anchor) {
      setPosition((current) => current.ready ? { ...current, ready: false } : current);
      return;
    }

    let focusFrame = 0;
    let hasFocused = false;
    const formViewport = anchor.closest<HTMLElement>("[data-dental-form-viewport]");
    const browserFrame = anchor.closest<HTMLElement>("[data-dental-scheduling-browser]");

    const viewportMetrics = () => {
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0;
      const left = viewport?.offsetLeft ?? 0;
      return { top, left, bottom: top + (viewport?.height ?? window.innerHeight), right: left + (viewport?.width ?? window.innerWidth) };
    };

    const updatePosition = () => {
      const viewport = viewportMetrics();
      const form = formViewport?.getBoundingClientRect() ?? viewport;
      const browser = browserFrame?.getBoundingClientRect() ?? viewport;
      const nextPosition = positionDentalPicker({
        anchor: anchor.getBoundingClientRect(),
        boundary: { top: Math.max(form.top, browser.top), left: Math.max(form.left, browser.left), bottom: Math.min(form.bottom, browser.bottom), right: Math.min(form.right, browser.right) },
        viewport,
        width,
        height: (panelRef.current?.scrollHeight ?? 300) + 2,
      });
      if (!nextPosition) {
        setPosition((current) => current.ready ? { ...current, ready: false } : current);
        return;
      }

      setPosition((current) => current.ready
        && current.top === nextPosition.top && current.left === nextPosition.left
        && current.maxHeight === nextPosition.maxHeight && current.panelWidth === nextPosition.panelWidth
        && current.placement === nextPosition.placement ? current : { ...nextPosition, ready: true });

      if (autoFocus && !hasFocused) {
        hasFocused = true;
        focusFrame = window.requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>("[data-popover-autofocus]")?.focus({ preventScroll: true }));
      }
    };

    const frame = window.requestAnimationFrame(updatePosition);
    const handlePointerDown = (event: PointerEvent) => {
      if (presentation) return;
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchor.contains(target)) return;
      onCloseRef.current();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (presentation) return;
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCloseRef.current();
      focusFrame = window.requestAnimationFrame(() => anchor.focus({ preventScroll: true }));
    };
    const handleViewportChange = () => updatePosition();
    const resizeObserver = new ResizeObserver(handleViewportChange);
    if (formViewport) resizeObserver.observe(formViewport);
    if (browserFrame) resizeObserver.observe(browserFrame);
    if (panelRef.current) {
      resizeObserver.observe(panelRef.current);
      Array.from(panelRef.current.children).forEach((child) => resizeObserver.observe(child));
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, { capture: true, passive: true });
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);

    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(focusFrame);
      resizeObserver.disconnect();
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
    };
  }, [anchor, autoFocus, mounted, open, presentation, width]);

  if (!mounted || !open || !anchor) return null;

  return createPortal(
    <motion.div
      ref={panelRef}
      id={panelId}
      data-floating-panel={ariaLabel}
      data-placement={position.placement}
      role={presentation ? "presentation" : "dialog"}
      aria-label={presentation ? undefined : ariaLabel}
      aria-hidden={presentation ? true : undefined}
      inert={presentation ? true : undefined}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed z-[140] overflow-x-hidden overflow-y-auto rounded-[12px] border border-[#d5deea] bg-white shadow-[0_18px_48px_rgba(15,23,42,.2)] ${presentation ? "pointer-events-none select-none" : ""}`}
      style={{
        top: position.top,
        left: position.left,
        width: position.panelWidth,
        maxHeight: position.maxHeight || undefined,
        visibility: position.ready ? "visible" : "hidden",
      }}
    >
      {children}
    </motion.div>,
    document.body,
  );
}

function CalendarPicker({
  animationActive = true,
  selected,
  cursor,
  onCursorChange,
  onSelect,
  autoStage = "idle",
  readOnly = false,
}: {
  animationActive?: boolean;
  selected: VisitDate | null;
  cursor: { year: number; month: number };
  onCursorChange: (next: { year: number; month: number }) => void;
  onSelect: (date: VisitDate) => void;
  autoStage?: AutoDateStage;
  readOnly?: boolean;
}) {
  const cells = useMemo(() => buildCalendarCells(cursor.year, cursor.month), [cursor.month, cursor.year]);
  const stageCopy: Record<Exclude<AutoDateStage, "idle">, string> = {
    requested: "Requested date · Tue, Aug 25",
    checking: "Checking Aug 25 · 4:30 PM…",
    unavailable: "Availability checked",
    preference: "Preference updated · weekdays after 4:30 PM",
    searching: "Searching the next weekday opening…",
    matched: "Thu, Aug 27 · date selected",
  };

  const changeMonth = (delta: number) => {
    const date = new Date(Date.UTC(cursor.year, cursor.month + delta, 1));
    onCursorChange({ year: date.getUTCFullYear(), month: date.getUTCMonth() });
  };

  return (
    <div className="p-2.5">
      <div className="flex items-center justify-between">
        <button type="button" aria-label="Previous month" disabled={readOnly} tabIndex={readOnly ? -1 : undefined} onClick={readOnly ? undefined : () => changeMonth(-1)} className={`flex h-6 w-6 items-center justify-center rounded-[7px] border border-[#e0e7ef] text-[#667085] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/35 ${readOnly ? "cursor-default bg-[#f8fafc]" : "transition hover:bg-[#f4f7fb]"}`}>
          <ChevronLeft className="h-3 w-3" />
        </button>
        <div className="text-[10px] font-bold text-[#152238]">{monthNames[cursor.month]} {cursor.year}</div>
        <button type="button" aria-label="Next month" disabled={readOnly} tabIndex={readOnly ? -1 : undefined} onClick={readOnly ? undefined : () => changeMonth(1)} className={`flex h-6 w-6 items-center justify-center rounded-[7px] border border-[#e0e7ef] text-[#667085] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/35 ${readOnly ? "cursor-default bg-[#f8fafc]" : "transition hover:bg-[#f4f7fb]"}`}>
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-7 text-center text-[7px] font-bold text-[#8793a5]">
        {weekdayHeadings.map((heading) => <div key={heading}>{heading}</div>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-y-0.5">
        {cells.map((cell, index) => {
          const isSelected = selected?.year === cell.year && selected.month === cell.month && selected.day === cell.day;
          const isRequestedDate = cell.year === 2026 && cell.month === 7 && cell.day === 25;
          const showUnavailableState = isRequestedDate && autoStage === "unavailable";
          const isWeekend = cell.weekday === "Sunday";
          const disabled = !cell.inCurrentMonth || isWeekend;
          return (
            <motion.button
              key={`${cell.year}-${cell.month}-${cell.day}-${index}`}
              type="button"
              role="gridcell"
              aria-label={`${cell.weekday}, ${cell.label}, ${cell.year}${showUnavailableState ? ", unavailable" : ""}`}
              aria-selected={isSelected}
              aria-disabled={readOnly || disabled}
              disabled={readOnly || disabled}
              tabIndex={readOnly ? -1 : undefined}
              onClick={readOnly ? undefined : () => onSelect(cell)}
              animate={animationActive && isSelected && autoStage === "matched"
                ? { scale: [1, 1.16, 1], boxShadow: "0 4px 10px rgba(79,70,200,.22)" }
                : animationActive && isSelected && autoStage === "checking"
                  ? { scale: [1, 1.08, 1] }
                  : { scale: 1, opacity: 1 }}
              transition={animationActive && isSelected && autoStage === "checking"
                ? { duration: 1, repeat: Infinity, ease: "easeInOut" }
                : { duration: animationActive ? .45 : 0, ease: [0.22, 1, 0.36, 1] }}
              className={`mx-auto flex h-6 w-6 items-center justify-center rounded-full text-[8.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/35 ${isSelected && showUnavailableState ? "bg-[#eef1f5] text-[#667085]" : isSelected ? "bg-[#4f46c8] text-white shadow-[0_4px_10px_rgba(79,70,200,.22)]" : disabled ? "cursor-default text-[#c6ced8]" : readOnly ? "cursor-default text-[#263449]" : "text-[#263449] hover:bg-[#eef2ff] hover:text-[#4338ca]"}`}
            >
              {cell.day}
            </motion.button>
          );
        })}
      </div>
      {autoStage !== "idle" ? (
        <motion.div key={autoStage} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} className={`mt-1.5 flex min-h-6 items-center gap-1.5 rounded-[7px] px-2 text-[7px] font-bold ${autoStage === "matched" ? "bg-[#eef2ff] text-[#3730a3]" : "bg-[#f7f8fb] text-[#667085]"}`}>
          <motion.span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${autoStage === "unavailable" ? "bg-[#98a2b3]" : "bg-[#4f46c8]"}`} animate={animationActive && (autoStage === "checking" || autoStage === "searching") ? { opacity: [.35, 1, .35], scale: [.8, 1.15, .8] } : { opacity: 1, scale: 1 }} transition={animationActive ? { duration: 1, repeat: autoStage === "checking" || autoStage === "searching" ? Infinity : 0 } : { duration: 0 }} />
          {stageCopy[autoStage]}
        </motion.div>
      ) : (
        <div className="mt-1.5 flex items-center gap-2.5 border-t border-[#e7ecf2] pt-1.5 text-[7px] font-semibold text-[#667085]">
          <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#4f46c8]" />Selected</span>
          <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#d7dee8]" />Unavailable</span>
        </div>
      )}
    </div>
  );
}

function TimePicker({
  animationActive = true,
  title,
  options,
  selected,
  onSelect,
  autoStage = "idle",
  readOnly = false,
}: {
  animationActive?: boolean;
  title: string;
  options: Array<{ value: string; disabled: boolean }>;
  selected: string | null;
  onSelect: (value: string) => void;
  autoStage?: AutoTimeStage;
  readOnly?: boolean;
}) {
  const stageCopy: Record<Exclude<AutoTimeStage, "idle">, string> = {
    requested: "Requested time · 4:30 PM",
    unavailable: "4:30 PM is unavailable",
    selecting: "Selecting the matched opening…",
    matched: "4:45 PM · selected automatically",
  };

  return (
    <div className="p-2.5">
      <div className="flex items-center justify-between gap-2.5 border-b border-[#e7ecf2] pb-2.5">
        <div>
          <div className="text-[10.5px] font-bold text-[#152238]">{title}</div>
          <div className="mt-0.5 text-[8px] font-medium text-[#7d8999]">15-minute availability</div>
        </div>
        <Clock3 className="h-3.5 w-3.5 text-[#4f46c8]" />
      </div>
      <div role="listbox" aria-label={title} onKeyDown={moveListboxFocus} className="mt-2.5 grid max-h-[188px] grid-cols-2 gap-1.5 overflow-y-auto pr-1">
        {options.map((option, index) => {
          const unavailable = option.disabled;
          const active = selected === option.value && !unavailable;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={active}
              aria-disabled={readOnly || option.disabled}
              disabled={readOnly || option.disabled}
              tabIndex={readOnly ? -1 : undefined}
              data-popover-autofocus={!readOnly && index === 0 ? "true" : undefined}
              onClick={readOnly ? undefined : () => onSelect(option.value)}
              className={`flex h-[30px] items-center justify-between gap-1 rounded-[7px] border px-2.5 text-[8.5px] font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30 ${unavailable ? "cursor-default border-[#e2e7ed] bg-[#f5f6f8] text-[#98a2b3]" : active ? "border-[#4f46c8] bg-[#eef2ff] text-[#3730a3]" : option.disabled ? "cursor-default border-[#edf0f4] bg-[#f7f8fa] text-[#b0b8c3]" : readOnly ? "cursor-default border-[#dfe5ed] bg-white text-[#344054]" : "border-[#dfe5ed] bg-white text-[#344054] hover:border-[#aeb8e8] hover:bg-[#fafbff]"}`}
            >
              <span>{option.value}</span>
              {active ? <Check className="h-3.5 w-3.5" /> : null}
            </button>
          );
        })}
      </div>
      {autoStage !== "idle" && autoStage !== "unavailable" ? (
        <motion.div key={autoStage} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} className={`mt-2.5 flex min-h-[26px] items-center gap-1.5 rounded-[7px] px-2.5 text-[7.5px] font-bold ${autoStage === "matched" ? "bg-[#eef2ff] text-[#3730a3]" : "bg-[#f7f8fb] text-[#667085]"}`}>
          <motion.span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#4f46c8]" animate={animationActive && (autoStage === "requested" || autoStage === "selecting") ? { opacity: [.45, 1, .45] } : { opacity: 1 }} transition={animationActive ? { duration: .9, repeat: autoStage === "requested" || autoStage === "selecting" ? Infinity : 0 } : { duration: 0 }} />
          {stageCopy[autoStage]}
        </motion.div>
      ) : autoStage === "idle" ? <div className="mt-2.5 rounded-[7px] bg-[#f7f8fb] px-2.5 py-1.5 text-[8px] font-semibold text-[#697586]">Unavailable times cannot be selected.</div> : null}
    </div>
  );
}

function DurationPicker({ selected, onSelect }: { selected: number | null; onSelect: (value: number) => void }) {
  return (
    <div className="p-2.5">
      <div className="border-b border-[#e7ecf2] pb-2.5 text-[10.5px] font-bold text-[#152238]">Visit duration</div>
      <div role="listbox" aria-label="Visit duration" onKeyDown={moveListboxFocus} className="mt-2.5 space-y-1">
        {durationOptions.map((option, index) => {
          const active = selected === option.minutes;
          return (
            <button
              key={option.minutes}
              type="button"
              role="option"
              aria-selected={active}
              data-popover-autofocus={index === 0 ? "true" : undefined}
              onClick={() => onSelect(option.minutes)}
              className={`flex h-8 w-full items-center justify-between rounded-[7px] px-2.5 text-[9px] font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30 ${active ? "bg-[#eef2ff] text-[#3730a3]" : "text-[#344054] hover:bg-[#f5f7fa]"}`}
            >
              {option.value}
              {active ? <Check className="h-3.5 w-3.5" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EntityPicker({
  title,
  searchPlaceholder,
  actionLabel,
  options,
  selectedId,
  onSelect,
}: {
  title: string;
  searchPlaceholder: string;
  actionLabel: string;
  options: EntityOption[];
  selectedId?: string;
  onSelect: (option: EntityOption) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = options.filter((option) => `${option.title} ${option.subtitle} ${option.meta ?? ""}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div>
      <div className="border-b border-[#e3e8ef] p-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10.5px] font-bold text-[#152238]">{title}</div>
          <button type="button" className="inline-flex h-7 items-center gap-1 rounded-[7px] border border-[#dfe5ed] px-2 text-[8px] font-bold text-[#4338ca] transition hover:bg-[#f7f8ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30"><Plus className="h-2.5 w-2.5" />{actionLabel}</button>
        </div>
        <div className="relative mt-2.5">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-[#98a2b3]" />
          <input
            data-popover-autofocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 w-full rounded-[7px] border border-[#dfe5ed] bg-[#fbfcfe] pl-8 pr-2.5 text-[9px] font-semibold text-[#344054] outline-none transition placeholder:text-[#98a2b3] focus:border-[#818cf8] focus:ring-2 focus:ring-[#4f46c8]/10"
          />
        </div>
      </div>
      <div role="listbox" aria-label={title} onKeyDown={moveListboxFocus} className="max-h-[216px] overflow-y-auto p-1.5">
        {filtered.length ? filtered.map((option) => {
          const active = selectedId === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => onSelect(option)}
              className={`flex w-full items-center gap-2.5 rounded-[8px] px-2 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30 ${active ? "bg-[#eef2ff]" : "hover:bg-[#f6f8fb]"}`}
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[8.5px] font-extrabold ${active ? "bg-[#4f46c8] text-white" : "bg-[#eef2f6] text-[#536275]"}`}>{option.initials}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[9.5px] font-bold text-[#152238]">{option.title}</span>
                <span className="mt-0.5 block truncate text-[8px] font-medium text-[#667085]">{option.subtitle}</span>
                {option.meta ? <span className={`mt-0.5 block truncate text-[7.5px] font-semibold ${option.meta.startsWith("Available") ? "text-[#087a5b]" : "text-[#8b96a6]"}`}>{option.meta}</span> : null}
              </span>
              {active ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#4f46c8]" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#b1bac5]" />}
            </button>
          );
        }) : <div className="px-2.5 py-7 text-center text-[9px] font-semibold text-[#98a2b3]">No matching results</div>}
      </div>
    </div>
  );
}

function RoomPicker({ selectedRoom, onSelect }: { selectedRoom: string; onSelect: (option: typeof roomOptions[number]) => void }) {
  return (
    <div className="p-1.5">
      <div className="px-2 pb-1.5 pt-1 text-[10px] font-bold text-[#152238]">Room / branch</div>
      <div role="listbox" aria-label="Room and branch" onKeyDown={moveListboxFocus}>
        {roomOptions.map((option, index) => {
          const active = selectedRoom === option.room;
          return (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={active}
              data-popover-autofocus={index === 0 ? "true" : undefined}
              onClick={() => onSelect(option)}
              className={`flex w-full items-center gap-2 rounded-[7px] px-2 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30 ${active ? "bg-[#eef2ff]" : "hover:bg-[#f6f8fb]"}`}
            >
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] ${active ? "bg-[#4f46c8] text-white" : "bg-[#eef2f6] text-[#64748b]"}`}><Building2 className="h-3.5 w-3.5" /></span>
              <span className="min-w-0 flex-1"><span className="block truncate text-[9px] font-bold text-[#152238]">{option.room}</span><span className="block truncate text-[8px] font-medium text-[#667085]">{option.branch} · {option.meta}</span></span>
              {active ? <Check className="h-4 w-4 text-[#4f46c8]" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PracticeCalendarBackdrop({ onCreate, appointmentCreated = false }: { onCreate?: () => void; appointmentCreated?: boolean }) {
  const times = ["3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM"];
  return (
    <div data-calendar-view={appointmentCreated ? "appointment-created" : "availability"} className="absolute inset-0 overflow-hidden bg-[#edf2f7] text-[#536275]" aria-hidden={onCreate ? undefined : true}>
      <div className="flex h-12 items-center justify-between border-b border-[#d7e0e9] bg-white/90 px-3">
        <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#4f46c8]" /><span className="text-[12px] font-bold text-[#152238]">Calendar</span><span className="rounded-md bg-[#f0f3f7] px-2 py-1 text-[9px] font-semibold">Aug 24 – Aug 30, 2026</span></div>
        <div className="flex items-center gap-1.5">
          {appointmentCreated ? (
            <button
              type="button"
              disabled
              data-calendar-create="display-only"
              className="mr-1 inline-flex h-7 cursor-default items-center gap-1 rounded-[7px] bg-[#101828] px-2.5 text-[9px] font-bold text-white shadow-[0_3px_8px_rgba(16,24,40,.14)]"
            >
              <Plus className="h-3 w-3" />
              Create
            </button>
          ) : onCreate ? (
            <button type="button" onClick={onCreate} className="mr-1 inline-flex h-7 items-center gap-1 rounded-[7px] bg-[#101828] px-2.5 text-[9px] font-bold text-white">
              <Plus className="h-3 w-3" />
              Create
            </button>
          ) : null}
          <span className="rounded-md bg-white px-2 py-1 text-[9px] font-bold">Day</span>
          <span className="rounded-md bg-[#dfe5ed] px-2 py-1 text-[9px] font-bold text-[#152238]">Week</span>
          <span className="rounded-md bg-white px-2 py-1 text-[9px] font-bold">Month</span>
        </div>
      </div>
      <div className="grid h-[calc(100%-48px)] grid-cols-[48px_repeat(5,1fr)] bg-white/55">
        <div className="border-r border-[#dce4ed] pt-7">{times.map((time) => <div key={time} className="h-12 border-b border-[#e3e9f0] pr-2 text-right text-[8px] font-semibold text-[#8693a3]">{time}</div>)}</div>
        {["Mon 24", "Tue 25", "Wed 26", "Thu 27", "Fri 28"].map((day, column) => <div key={day} className="relative border-r border-[#dce4ed]"><div className="h-7 border-b border-[#dce4ed] text-center text-[8px] font-bold text-[#64748b]">{day}</div>{times.map((time) => <div key={time} className="h-12 border-b border-[#e3e9f0]" />)}{column === 1 ? <div className="absolute left-1 right-1 top-[75px] rounded-md border-l-2 border-[#64748b] bg-[#eef2f6] px-1.5 py-1 text-[7.5px] font-bold text-[#475467]">Hygiene recall · 4:00 PM</div> : null}{column === 3 ? <motion.div key={appointmentCreated ? "created-visit" : "available-opening"} data-calendar-booking={appointmentCreated ? "Jordan Lee" : undefined} initial={{ opacity: 0, scale: .97 }} animate={{ opacity: 1, scale: 1 }} className={`absolute left-1 right-1 top-[145px] rounded-md border-l-2 px-1.5 py-1 ${appointmentCreated ? "h-[96px] border-[#4f46c8] bg-[#eef2ff] text-[#3730a3] shadow-[0_5px_14px_rgba(79,70,200,.14)]" : "border-[#087a5b] bg-[#e6f7f1] text-[#087a5b]"}`}><div className="text-[7.5px] font-bold">{appointmentCreated ? "Jordan Lee" : "Available · 4:45 PM"}</div>{appointmentCreated ? <div className="mt-0.5 text-[6.5px] font-semibold leading-[9px]">Routine cleaning<br />4:45–5:45 PM · Dr. Maya Chen</div> : null}</motion.div> : null}</div>)}
      </div>
    </div>
  );
}

function TimeFieldButton({
  anchorRef,
  label,
  value,
  detail,
  icon: Icon,
  open,
  onClick,
}: {
  anchorRef: (node: HTMLButtonElement | null) => void;
  label: string;
  value: string;
  detail?: string;
  icon: typeof CalendarDays;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <button
      ref={anchorRef}
      type="button"
      aria-expanded={open}
      data-time-field={label.toLowerCase()}
      onClick={onClick}
      className={`group grid h-[54px] min-w-0 grid-cols-[32px_minmax(0,1fr)_24px] items-center overflow-hidden rounded-[8px] border p-0 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30 ${open ? "border-[#6366f1] bg-[#f7f8ff] shadow-[0_0_0_1px_rgba(99,102,241,.08)]" : "border-[#dce4ed] bg-white hover:border-[#b9c4d1]"}`}
    >
      <span className="flex h-8 w-8 items-center justify-center border-r border-[#e6ebf1] text-[#4f46c8]"><Icon className="h-3.5 w-3.5" /></span>
      <span className="grid min-w-0 content-center px-2 py-1">
        <span className="block truncate text-[7.5px] font-bold uppercase tracking-[.06em] text-[#8a96a6]">{label}</span>
        <span title={value} className={`mt-0.5 block truncate text-[10px] font-bold leading-[12px] tabular-nums ${value === "Select" ? "text-[#98a2b3]" : "text-[#152238]"}`}>{value}</span>
        <span aria-hidden={!detail} className={`mt-0.5 block h-[9px] truncate text-[7px] font-medium leading-[9px] ${detail ? "text-[#667085]" : "invisible"}`}>{detail || "\u00a0"}</span>
      </span>
      <span className="flex h-full w-6 items-center justify-center">
        <ChevronDown className={`h-3 w-3 text-[#98a2b3] transition ${open ? "rotate-180 text-[#4f46c8]" : "group-hover:text-[#667085]"}`} />
      </span>
    </button>
  );
}

function SelectionRow({
  anchorRef,
  icon: Icon,
  label,
  value,
  detail,
  open,
  onClick,
}: {
  anchorRef: (node: HTMLButtonElement | null) => void;
  icon: typeof CalendarDays;
  label: string;
  value: string;
  detail: string;
  open: boolean;
  onClick: () => void;
}) {
  const empty = value.startsWith("Select") || value === "Without patient";
  return (
    <button ref={anchorRef} type="button" aria-expanded={open} onClick={onClick} className={`flex min-h-[48px] w-full items-center gap-2 rounded-[8px] border px-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/30 ${open ? "border-[#6366f1] bg-[#f7f8ff]" : "border-[#dce4ed] bg-white hover:border-[#b9c4d1]"}`}>
      <span className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] ${empty ? "bg-[#f0f3f7] text-[#98a2b3]" : "bg-[#eef2ff] text-[#4338ca]"}`}><Icon className="h-3.5 w-3.5" /></span>
      <span className="min-w-0 flex-1"><span className="block text-[7.5px] font-bold uppercase tracking-[.06em] text-[#8a96a6]">{label}</span><span className={`mt-0.5 block truncate text-[10.5px] font-bold ${empty ? "text-[#667085]" : "text-[#152238]"}`}>{value}</span><span className="mt-0.5 block truncate text-[8px] font-medium text-[#7b8797]">{detail}</span></span>
      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] border border-[#dce4ed] bg-[#fbfcfe]"><ChevronDown className={`h-[13px] w-[13px] text-[#98a2b3] transition ${open ? "rotate-180 text-[#4f46c8]" : ""}`} /></span>
    </button>
  );
}

function NewPatientModal({
  pageActive,
  stage,
  firstName,
  lastName,
  mobile,
}: {
  pageActive: boolean;
  stage: AutoPatientStage;
  firstName: string;
  lastName: string;
  mobile: string;
}) {
  const saving = stage === "saving";
  const confirmed = stage === "confirmed";
  const showCursor = (field: "first-name" | "last-name" | "mobile") => stage === field;

  return (
    <motion.div
      data-patient-modal={stage}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: .2 }}
      className="absolute inset-2.5 z-30 flex items-center justify-center rounded-[11px] bg-[#344054]/28 p-5 backdrop-blur-[1.5px]"
    >
      <motion.div
        initial={{ opacity: 0, y: 10, scale: .985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -4, scale: .99 }}
        transition={{ duration: .24, ease: [0.22, 1, 0.36, 1] }}
        className="w-[360px] overflow-hidden rounded-[12px] border border-[#d8e0e9] bg-white shadow-[0_24px_64px_rgba(15,23,42,.28)]"
      >
        <div className="flex items-center justify-between border-b border-[#e5eaf0] px-4 py-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-[#eef2ff] text-[#4338ca]"><UserRound className="h-3.5 w-3.5" /></span>
              <div><h5 className="text-[13px] font-bold tracking-[-.02em] text-[#101828]">New patient</h5><p className="mt-0.5 text-[8px] font-semibold text-[#7b8797]">Create a patient profile for this visit</p></div>
            </div>
          </div>
          <span className="flex h-7 w-7 items-center justify-center rounded-[8px] border border-[#dce4ed] bg-[#f8fafc] text-[#667085]"><X className="h-3 w-3" /></span>
        </div>

        <div className="space-y-2.5 px-4 py-3.5">
          <div className="grid grid-cols-2 gap-2.5">
            {[
              { label: "First name", value: firstName, field: "first-name" as const, placeholder: "First name" },
              { label: "Last name", value: lastName, field: "last-name" as const, placeholder: "Last name" },
            ].map((field) => (
              <div key={field.label}>
                <div className="mb-1 text-[7.5px] font-bold uppercase tracking-[.07em] text-[#8a96a6]">{field.label}</div>
                <div data-field={field.field} className={`flex h-10 items-center rounded-[8px] border px-2.5 text-[10px] font-bold ${showCursor(field.field) ? "border-[#6366f1] bg-[#f8f8ff] shadow-[0_0_0_2px_rgba(99,102,241,.08)]" : "border-[#dce4ed] bg-white"}`}>
                  <span className={field.value ? "text-[#152238]" : "text-[#b0b8c3]"}>{field.value || field.placeholder}</span>
                  {showCursor(field.field) ? <motion.span aria-hidden="true" className="ml-0.5 h-3.5 w-px bg-[#4f46c8]" animate={pageActive ? { opacity: [1, 0, 1] } : { opacity: 1 }} transition={pageActive ? { duration: .7, repeat: Infinity } : { duration: 0 }} /> : null}
                </div>
              </div>
            ))}
          </div>

          <div>
            <div className="mb-1 text-[7.5px] font-bold uppercase tracking-[.07em] text-[#8a96a6]">Mobile number</div>
            <div data-field="mobile" className={`flex h-10 items-center gap-2 rounded-[8px] border px-2.5 ${showCursor("mobile") ? "border-[#6366f1] bg-[#f8f8ff] shadow-[0_0_0_2px_rgba(99,102,241,.08)]" : "border-[#dce4ed] bg-white"}`}>
              <Phone className="h-3.5 w-3.5 text-[#667085]" />
              <span className={`text-[10px] font-bold ${mobile ? "text-[#152238]" : "text-[#b0b8c3]"}`}>{mobile || "+1 mobile number"}</span>
              {showCursor("mobile") ? <motion.span aria-hidden="true" className="h-3.5 w-px bg-[#4f46c8]" animate={pageActive ? { opacity: [1, 0, 1] } : { opacity: 1 }} transition={pageActive ? { duration: .7, repeat: Infinity } : { duration: 0 }} /> : null}
            </div>
          </div>

          <div className="rounded-[8px] border border-[#e5eaf0] bg-[#f8fafc] px-2.5 py-2 text-[8px] font-semibold leading-[12px] text-[#667085]">
            Contact details will be attached to this fictional appointment only.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#e5eaf0] bg-[#fbfcfe] px-4 py-3">
          <span className="inline-flex h-8 items-center rounded-[8px] border border-[#dce4ed] bg-white px-3 text-[8.5px] font-bold text-[#667085]">Cancel</span>
          <motion.span
            data-action="create-patient"
            data-state={confirmed ? "saved" : saving ? "pressing" : stage === "ready" ? "ready" : "idle"}
            animate={saving ? { scale: [1, .96, 1] } : { scale: 1 }}
            transition={{ duration: .34 }}
            className={`inline-flex h-8 min-w-[102px] items-center justify-center gap-1.5 rounded-[8px] px-3 text-[8.5px] font-bold text-white shadow-[0_6px_14px_rgba(15,23,42,.16)] ${confirmed ? "bg-[#3730a3]" : stage === "ready" || saving ? "bg-[#101828]" : "bg-[#cfd5dd]"}`}
          >
            {saving ? <LoaderCircle className={`h-3 w-3 ${pageActive ? "animate-spin" : ""}`} /> : confirmed ? <CheckCircle2 className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
            {saving ? "Creating…" : confirmed ? "Patient created" : "Create patient"}
          </motion.span>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function DentalSchedulingBrowser({ visibleMessageCount, transcriptLength, allowAutoPresentation, pageActive, onInteract }: { visibleMessageCount: number; transcriptLength: number; allowAutoPresentation: boolean; pageActive: boolean; onInteract: () => void }) {
  const [openPicker, setOpenPicker] = useState<PickerKey | null>(null);
  const [manualDraft, setManualDraft] = useState<VisitDraft | null>(null);
  const [calendarCursor, setCalendarCursor] = useState({ year: 2026, month: 7 });
  const [autoDateStage, setAutoDateStage] = useState<AutoDateStage>("idle");
  const [autoTimeStage, setAutoTimeStage] = useState<AutoTimeStage>("idle");
  const [autoWorkflowStage, setAutoWorkflowStage] = useState<AutoWorkflowStage>("idle");
  const [autoSelectingService, setAutoSelectingService] = useState(false);
  const [autoServiceSelected, setAutoServiceSelected] = useState(false);
  const [autoPatientStage, setAutoPatientStage] = useState<AutoPatientStage>("idle");
  const [autoVisitStage, setAutoVisitStage] = useState<AutoVisitStage>("idle");
  const [autoFirstName, setAutoFirstName] = useState("");
  const [autoLastName, setAutoLastName] = useState("");
  const [autoMobile, setAutoMobile] = useState("");
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [note, setNote] = useState("");
  const [visitPrepared, setVisitPrepared] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const anchors = useRef<Partial<Record<PickerKey, HTMLButtonElement | null>>>({});
  const formViewportRef = useRef<HTMLDivElement>(null);
  const patientSectionRef = useRef<HTMLDivElement>(null);
  const lowerFieldsRef = useRef<HTMLDivElement>(null);
  const serviceSectionRef = useRef<HTMLDivElement>(null);

  const presentationStage: AutoWorkflowStage = visibleMessageCount < 2 ? "idle" : autoWorkflowStage;
  const matchedDateVisible = visibleMessageCount >= 10 || presentationStage === "matched-date" || presentationStage === "matched-time" || presentationStage === "matched-complete";
  const matchedTimeVisible = visibleMessageCount >= 10 || presentationStage === "matched-time" || presentationStage === "matched-complete";
  const preferenceVisible = presentationStage === "preference" || presentationStage === "searching" || presentationStage === "matched-date";
  const requestedDateVisible = presentationStage !== "idle";
  const serviceSelected = autoServiceSelected || visibleMessageCount >= 5;
  const patientCreated = autoPatientStage === "confirmed" || autoPatientStage === "saved" || visibleMessageCount >= 13;
  const appointmentSubmitted = autoVisitStage === "submitted" || visibleMessageCount >= 13;
  const showNewPatientModal = autoPatientStage !== "idle" && autoPatientStage !== "saved";

  const autoDraft: VisitDraft = {
    date: matchedDateVisible ? matchedDate : requestedDateVisible ? requestedDate : null,
    start: matchedTimeVisible ? "4:45 PM" : null,
    end: matchedTimeVisible ? "5:45 PM" : null,
    duration: serviceSelected ? automatedService.duration : null,
    room: "Hygiene 1",
    branch: "North Office",
    patient: patientCreated ? patientOptions[0] : null,
    doctor: matchedTimeVisible ? doctorOptions[0] : null,
    service: serviceSelected ? automatedService : null,
  };
  const draft = manualDraft ?? autoDraft;
  const servicePresentation = autoSelectingService && openPicker === "service";
  const requestedTimeUnavailable = !manualDraft && presentationStage === "unavailable";
  const isComplete = Boolean(draft.date && draft.start && draft.end && draft.duration && draft.room && draft.patient && draft.doctor && draft.service);
  const progressReady = visibleMessageCount >= transcriptLength && appointmentSubmitted;

  useEffect(() => {
    if (visibleMessageCount !== 0) return;
    setManualDraft(null);
    setVisitPrepared(false);
    setOpenPicker(null);
    setAutoDateStage("idle");
    setAutoTimeStage("idle");
    setAutoWorkflowStage("idle");
    setAutoSelectingService(false);
    setAutoServiceSelected(false);
    setAutoPatientStage("idle");
    setAutoVisitStage("idle");
    setAutoFirstName("");
    setAutoLastName("");
    setAutoMobile("");
    setUserHasInteracted(false);
    setDismissed(false);
  }, [visibleMessageCount]);

  useEffect(() => {
    if (manualDraft || userHasInteracted || dismissed) return;

    if (!allowAutoPresentation) {
      setAutoDateStage("idle");
      setAutoTimeStage("idle");
      setAutoSelectingService(false);
      setOpenPicker(null);
      return;
    }

    const timers: number[] = [];
    const after = (delay: number, action: () => void) => {
      timers.push(window.setTimeout(action, delay));
    };
    const showDate = (workflow: AutoWorkflowStage, dateStage: AutoDateStage) => {
      setAutoSelectingService(false);
      setCalendarCursor({ year: 2026, month: 7 });
      setAutoWorkflowStage(workflow);
      setAutoDateStage(dateStage);
      setAutoTimeStage("idle");
      setOpenPicker("date");
    };
    const showTime = (workflow: AutoWorkflowStage, timeStage: AutoTimeStage) => {
      setAutoSelectingService(false);
      setAutoWorkflowStage(workflow);
      setAutoDateStage("idle");
      setAutoTimeStage(timeStage);
      setOpenPicker("start");
    };
    const closePresentation = (workflow: AutoWorkflowStage) => {
      setAutoSelectingService(false);
      setAutoWorkflowStage(workflow);
      setAutoDateStage("idle");
      setAutoTimeStage("idle");
      setOpenPicker(null);
    };

    if (visibleMessageCount <= 1) {
      closePresentation("idle");
    } else if (visibleMessageCount === 2) {
      after(180, () => showDate("requested-date", "requested"));
      after(850, () => showTime("requested-time", "requested"));
      after(1450, () => closePresentation("requested-time"));
    } else if (visibleMessageCount === 3) {
      // ClawEase asks which service the patient needs. Keep the service empty.
      after(0, () => closePresentation("requested-time"));
    } else if (visibleMessageCount === 4) {
      // Select the service only after the patient explicitly requests it.
      after(180, () => {
        setAutoWorkflowStage("requested-date");
        const viewport = formViewportRef.current;
        const target = serviceSectionRef.current;
        if (!viewport || !target) return;
        const viewportRect = viewport.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        viewport.scrollTo({ top: Math.max(0, viewport.scrollTop + targetRect.top - viewportRect.top - 12), behavior: "smooth" });
      });
      after(420, () => {
        setAutoSelectingService(true);
        setOpenPicker("service");
      });
      after(880, () => setAutoServiceSelected(true));
      after(1250, () => {
        closePresentation("requested-time");
      });
      after(1400, () => formViewportRef.current?.scrollTo({ top: 0, behavior: "smooth" }));
    } else if (visibleMessageCount === 5) {
      after(260, () => showDate("checking", "checking"));
      after(1200, () => showTime("unavailable", "unavailable"));
    } else if (visibleMessageCount === 6) {
      // The disabled 4:30 PM option was already shown 500ms before this
      // conversation reply. Preserve it here without replaying the warning.
      after(0, () => showTime("unavailable", "unavailable"));
    } else if (visibleMessageCount === 7) {
      after(250, () => closePresentation("preference"));
    } else if (visibleMessageCount === 8) {
      after(250, () => showDate("searching", "searching"));
    } else if (visibleMessageCount === 9) {
      after(250, () => showDate("matched-date", "matched"));
      after(900, () => showTime("matched-date", "selecting"));
      after(1800, () => showTime("matched-time", "matched"));
      after(2550, () => closePresentation("matched-complete"));
    } else {
      after(250, () => closePresentation("matched-complete"));
    }

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [allowAutoPresentation, dismissed, manualDraft, userHasInteracted, visibleMessageCount]);

  useEffect(() => {
    if (manualDraft || userHasInteracted || dismissed) return;

    if (!allowAutoPresentation) {
      if (visibleMessageCount >= 13) {
        setAutoFirstName("Jordan");
        setAutoLastName("Lee");
        setAutoMobile("+1 202-555-0147");
        setAutoPatientStage("saved");
        setAutoVisitStage("submitted");
        setVisitPrepared(true);
      }
      return;
    }

    if (visibleMessageCount >= 13) {
      setAutoFirstName("Jordan");
      setAutoLastName("Lee");
      setAutoMobile("+1 202-555-0147");
      setAutoPatientStage("saved");
      setAutoVisitStage("submitted");
      setVisitPrepared(true);
      return;
    }

    if (visibleMessageCount !== 12) return;

    const timers: number[] = [];
    const after = (delay: number, action: () => void) => {
      timers.push(window.setTimeout(action, delay));
    };
    const typeValue = (value: string, startsAt: number, interval: number, update: (next: string) => void) => {
      Array.from(value).forEach((_, index) => {
        after(startsAt + (index * interval), () => update(value.slice(0, index + 1)));
      });
    };
    const scrollFormTo = (target: HTMLElement | null) => {
      const viewport = formViewportRef.current;
      if (!viewport || !target) return;
      const viewportRect = viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = Math.max(0, viewport.scrollTop + targetRect.top - viewportRect.top - 14);
      viewport.scrollTo({ top: targetTop, behavior: "smooth" });
    };

    setAutoPatientStage("idle");
    setAutoVisitStage("idle");
    setAutoFirstName("");
    setAutoLastName("");
    setAutoMobile("");
    setVisitPrepared(false);

    after(180, () => scrollFormTo(patientSectionRef.current));
    after(520, () => setAutoPatientStage("opening"));
    after(820, () => setAutoPatientStage("first-name"));
    typeValue("Jordan", 900, 55, setAutoFirstName);
    after(1370, () => setAutoPatientStage("last-name"));
    typeValue("Lee", 1450, 65, setAutoLastName);
    after(1750, () => setAutoPatientStage("mobile"));
    typeValue("+1 202-555-0147", 1830, 45, setAutoMobile);
    after(2750, () => setAutoPatientStage("ready"));
    after(3200, () => setAutoPatientStage("saving"));
    after(3650, () => setAutoPatientStage("confirmed"));
    after(4100, () => setAutoPatientStage("saved"));
    after(4200, () => formViewportRef.current?.scrollTo({ top: 0, behavior: "smooth" }));
    after(4350, () => setAutoVisitStage("ready"));
    after(5000, () => setAutoVisitStage("pressing"));
    after(5400, () => setAutoVisitStage("submitting"));
    after(5900, () => {
      setAutoVisitStage("submitted");
      setVisitPrepared(true);
    });

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [allowAutoPresentation, dismissed, manualDraft, userHasInteracted, visibleMessageCount]);

  useEffect(() => {
    if (!allowAutoPresentation || manualDraft || userHasInteracted || dismissed) return;
    const viewport = formViewportRef.current;
    if (!viewport) return;

    const frame = window.requestAnimationFrame(() => {
      let target: HTMLElement | null = null;
      if (visibleMessageCount >= 11 && visibleMessageCount <= 12) target = patientSectionRef.current;

      if (!target) {
        viewport.scrollTo({ top: 0, behavior: visibleMessageCount === 0 ? "auto" : "smooth" });
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = Math.max(0, viewport.scrollTop + targetRect.top - viewportRect.top - 14);
      viewport.scrollTo({ top: targetTop, behavior: "smooth" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [allowAutoPresentation, dismissed, manualDraft, userHasInteracted, visibleMessageCount]);

  useEffect(() => {
    if (!appointmentSubmitted || dismissed) return;
    const timer = window.setTimeout(() => {
      setOpenPicker(null);
      setDismissed(true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [appointmentSubmitted, dismissed]);

  const updateDraft = (patch: Partial<VisitDraft>) => {
    setManualDraft((current) => ({ ...(current ?? autoDraft), ...patch }));
    setVisitPrepared(false);
  };

  const markUserInteraction = () => {
    setUserHasInteracted(true);
    setAutoDateStage("idle");
    setAutoTimeStage("idle");
    setAutoSelectingService(false);
    onInteract();
  };

  const togglePicker = (picker: PickerKey) => {
    markUserInteraction();
    setVisitPrepared(false);
    setOpenPicker((current) => current === picker ? null : picker);
  };

  const closePicker = () => setOpenPicker(null);
  const setAnchor = (picker: PickerKey) => (node: HTMLButtonElement | null) => { anchors.current[picker] = node; };

  const selectDate = (date: VisitDate) => {
    updateDraft({ date });
    closePicker();
  };

  const selectStart = (start: string) => {
    const duration = draft.duration ?? 45;
    updateDraft({ start, duration, end: minutesToTime(timeToMinutes(start) + duration) });
    closePicker();
  };

  const selectEnd = (end: string) => {
    const startMinutes = draft.start ? timeToMinutes(draft.start) : timeToMinutes("4:45 PM");
    const duration = Math.max(15, timeToMinutes(end) - startMinutes);
    updateDraft({ start: draft.start ?? "4:45 PM", end, duration });
    closePicker();
  };

  const selectDuration = (duration: number) => {
    const start = draft.start ?? "4:45 PM";
    updateDraft({ start, duration, end: minutesToTime(timeToMinutes(start) + duration) });
    closePicker();
  };

  const timeStatus = manualDraft
    ? "Editing visit"
    : visibleMessageCount >= 10
      ? "Confirmed by patient"
      : presentationStage === "matched-date" || presentationStage === "matched-time" || presentationStage === "matched-complete"
        ? "Opening matched"
        : presentationStage === "searching"
          ? "Finding next opening"
          : presentationStage === "preference"
            ? "Preference updated"
            : presentationStage === "unavailable"
              ? "Availability checked"
              : presentationStage === "checking"
                ? "Checking calendar"
                : presentationStage === "requested-date" || presentationStage === "requested-time"
                  ? "Request captured"
                  : "Waiting";
  const visitStatusLabel = appointmentSubmitted ? "Booked" : patientCreated ? "Ready" : "Draft";
  const visitSubmitting = autoVisitStage === "submitting";
  const visitButtonLabel = appointmentSubmitted ? "Visit created" : visitSubmitting ? "Creating…" : "Create Visit";
  const visitActionState = appointmentSubmitted
    ? "created"
    : autoVisitStage === "submitting"
      ? "submitting"
      : autoVisitStage === "pressing"
        ? "pressing"
        : isComplete
          ? "ready"
          : "disabled";
  const liveStatus = progressReady
    ? "The fictional appointment is submitted and the sample conversation is complete."
    : appointmentSubmitted
      ? "The fictional appointment has been submitted."
      : patientCreated
        ? "The fictional patient record has been created."
        : `The fictional dental form is updating from conversation message ${visibleMessageCount} of ${transcriptLength}.`;

  return (
    <>
    <div
      data-dental-scheduling-browser
      data-workflow-stage={autoWorkflowStage}
      data-auto-date-stage={autoDateStage}
      data-patient-stage={autoPatientStage}
      data-booking-stage={autoVisitStage}
      aria-hidden="true"
      inert={true}
      className="pointer-events-none select-none overflow-hidden rounded-[15px] border border-[#cbd5e1] bg-[#e8edf3] shadow-[0_20px_48px_rgba(15,23,42,.18)]"
    >
      <div className="flex h-8 items-center gap-2 border-b border-[#cfd8e3] bg-[#eef2f6] px-2.5">
        <div className="flex shrink-0 items-center gap-1" aria-hidden="true"><span className="h-2 w-2 rounded-full bg-[#c2ccd8]" /><span className="h-2 w-2 rounded-full bg-[#b5c0cd]" /><span className="h-2 w-2 rounded-full bg-[#a9b5c3]" /></div>
        <div className="mx-auto flex h-5 min-w-0 max-w-[320px] flex-1 items-center justify-center gap-1 rounded-[6px] border border-[#d5dde7] bg-white px-2.5 text-[8px] font-medium text-[#667085] shadow-[0_1px_2px_rgba(15,23,42,.04)]"><Lock className="h-2.5 w-2.5 shrink-0" /><span className="truncate">app.example-dental.example/calendar</span></div>
        <span className="w-8 shrink-0 text-right text-[10px] font-bold tracking-[.18em] text-[#8996a6]" aria-hidden="true">···</span>
      </div>

      <div className="relative h-[var(--dental-scheduling-height,500px)] overflow-hidden bg-[#dfe5ed]">
        <PracticeCalendarBackdrop appointmentCreated={appointmentSubmitted} onCreate={dismissed && !appointmentSubmitted ? () => setDismissed(false) : undefined} />
        <AnimatePresence initial={false}>
          {!dismissed ? (
          <motion.div
            key="create-visit-layer"
            className="absolute inset-0"
            initial={{ opacity: 1, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: .985, y: 8 }}
            transition={{ duration: .28, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="absolute inset-0 bg-[#344054]/24 backdrop-blur-[1px]" aria-hidden="true" />
            <div className="absolute inset-0 p-2.5">
              <div className="flex h-full min-w-[640px] flex-col overflow-hidden rounded-[11px] border border-[#d2dbe6] bg-white shadow-[0_22px_60px_rgba(15,23,42,.24)]">
                <div className="flex min-h-[52px] shrink-0 items-center justify-between gap-2.5 border-b border-[#e1e7ee] bg-white px-3 py-2">
                  <div className="min-w-0"><div className="flex items-center gap-1.5"><h4 className="truncate text-[15px] font-bold tracking-[-.025em] text-[#101828]">Create Visit</h4><span className={`rounded-full px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-[.08em] ${appointmentSubmitted ? "bg-[#eef2ff] text-[#3730a3]" : patientCreated ? "bg-[#f3f0ff] text-[#4f46c8]" : "border border-[#e1e7ee] bg-[#f8fafc] text-[#667085]"}`}>{visitStatusLabel}</span></div><p className="mt-0.5 text-[8.5px] font-semibold text-[#667085]">{draft.date && draft.start && draft.end ? `${draft.date.label}, ${draft.date.year} · ${draft.start} – ${draft.end}` : "Select the visit details"}</p></div>
                  <div className="flex shrink-0 items-center gap-2">
                    <motion.button
                      type="button"
                      data-action="create-visit"
                      data-state={visitActionState}
                      disabled={!isComplete && !appointmentSubmitted}
                      onClick={() => { setAutoVisitStage("submitted"); setVisitPrepared(true); }}
                      animate={autoVisitStage === "pressing"
                        ? { scale: [1, .94, 1], boxShadow: ["0 6px 14px rgba(15,23,42,.16)", "0 0 0 4px rgba(79,70,200,.18)", "0 6px 14px rgba(15,23,42,.16)"] }
                        : autoVisitStage === "ready"
                          ? { scale: 1, boxShadow: "0 0 0 3px rgba(79,70,200,.14)" }
                          : { scale: 1, boxShadow: "0 6px 14px rgba(15,23,42,.16)" }}
                      transition={{ duration: .42, ease: [0.22, 1, 0.36, 1] }}
                      className={`inline-flex h-8 min-w-[92px] items-center justify-center gap-1 rounded-[8px] px-3 text-[8.5px] font-bold text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#475467]/35 disabled:cursor-not-allowed disabled:bg-[#dfe3e8] disabled:text-[#98a2b3] disabled:shadow-none ${appointmentSubmitted ? "bg-[#3730a3]" : "bg-[#101828]"}`}
                    >
                      {visitSubmitting ? <LoaderCircle className={`h-3 w-3 ${pageActive ? "animate-spin" : ""}`} /> : <CheckCircle2 className="h-3 w-3" />}
                      {visitButtonLabel}
                    </motion.button>
                    <button type="button" aria-label="Close create visit" onClick={() => { markUserInteraction(); closePicker(); setDismissed(true); }} className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#d7e0e9] bg-[#f8fafc] text-[#344054] transition hover:bg-[#eef2f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#64748b]/30"><X className="h-3.5 w-3.5" /></button>
                  </div>
                </div>

                <div ref={formViewportRef} data-dental-form-viewport className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_176px] overscroll-contain overflow-y-auto scroll-smooth bg-white" style={{ scrollbarWidth: "thin", scrollbarColor: "#c8d1dc transparent" }}>
                  <div className="min-w-0 p-2.5">
                    <div className="border-b border-[#e5eaf0] pb-2.5">
                      <div className="mb-2 flex items-center justify-between gap-2.5"><p className="text-[9.5px] font-bold text-[#344054]">Visit time</p><span className={`text-[7.5px] font-bold uppercase tracking-[.07em] ${requestedTimeUnavailable ? "text-[#667085]" : draft.date && draft.start ? "text-[#4338ca]" : "text-[#98a2b3]"}`}>{timeStatus}</span></div>
                      <div className="grid grid-cols-[repeat(4,minmax(0,1fr))] gap-1.5">
                        <TimeFieldButton anchorRef={setAnchor("date")} label="Date" value={draft.date?.label ?? "Select"} detail={draft.date?.weekday} icon={CalendarDays} open={openPicker === "date"} onClick={() => togglePicker("date")} />
                        <TimeFieldButton anchorRef={setAnchor("start")} label="Start" value={draft.start ?? "Select"} icon={Clock3} open={openPicker === "start"} onClick={() => togglePicker("start")} />
                        <TimeFieldButton anchorRef={setAnchor("end")} label="End" value={draft.end ?? "Select"} icon={Clock3} open={openPicker === "end"} onClick={() => togglePicker("end")} />
                        <TimeFieldButton anchorRef={setAnchor("duration")} label="Duration" value={draft.duration ? `${draft.duration} min` : "Select"} icon={Clock3} open={openPicker === "duration"} onClick={() => togglePicker("duration")} />
                      </div>
                      <div className="mt-2 grid h-8 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-[8px] border border-[#e1e7ee] bg-[#fbfcfe] px-2.5 text-[8px]"><span className="min-w-0 truncate whitespace-nowrap font-semibold text-[#667085]">Earlier visit time <span className="text-[#b0b8c3]">· No earlier availability added</span></span><button type="button" onClick={() => togglePicker("start")} className="inline-flex shrink-0 items-center gap-1 font-bold text-[#4338ca] focus-visible:outline-none focus-visible:underline"><Plus className="h-2.5 w-2.5" />Add time</button></div>
                    </div>

                    <div className="mt-2.5">
                      <SelectionRow anchorRef={setAnchor("room")} icon={Building2} label="Room / branch" value={draft.room} detail={draft.branch} open={openPicker === "room"} onClick={() => togglePicker("room")} />
                    </div>

                    <div className="mt-2.5 space-y-2.5">
                      <div ref={patientSectionRef}><div className="mb-1 flex items-end justify-between"><div><div className="text-[9.5px] font-bold text-[#101828]">Patient</div><div className="text-[8px] font-medium text-[#667085]">Patient selection and contact details</div></div><button type="button" onClick={() => togglePicker("patient")} className="inline-flex items-center gap-1 text-[8px] font-bold text-[#4338ca]"><Plus className="h-2.5 w-2.5" />New patient</button></div><SelectionRow anchorRef={setAnchor("patient")} icon={UserRound} label="Patient" value={draft.patient?.title ?? "Select patient"} detail={draft.patient ? `${draft.patient.subtitle} · ${draft.patient.meta ?? ""}` : "You can continue without selecting a patient"} open={openPicker === "patient"} onClick={() => togglePicker("patient")} /></div>
                      <div ref={lowerFieldsRef} className="space-y-2.5">
                        <div><div className="mb-1 flex items-center justify-between"><div className="text-[9.5px] font-bold text-[#101828]">Assign</div><button type="button" onClick={() => togglePicker("doctor")} className="inline-flex items-center gap-1 text-[8px] font-bold text-[#4338ca]"><Plus className="h-2.5 w-2.5" />Add provider</button></div><SelectionRow anchorRef={setAnchor("doctor")} icon={Stethoscope} label="Doctor" value={draft.doctor?.title ?? "Select doctor"} detail={draft.doctor?.subtitle ?? "Choose the provider for this visit"} open={openPicker === "doctor"} onClick={() => togglePicker("doctor")} /></div>
                        <div ref={serviceSectionRef}><div className="mb-1 flex items-center justify-between"><div className="text-[9.5px] font-bold text-[#101828]">Group / reason</div><button type="button" onClick={() => togglePicker("service")} className="inline-flex items-center gap-1 text-[8px] font-bold text-[#4338ca]"><Search className="h-2.5 w-2.5" />Choose service</button></div><SelectionRow anchorRef={setAnchor("service")} icon={ClipboardList} label="Treatment / service" value={draft.service?.title ?? "Select treatment / service"} detail={draft.service ? `${draft.service.subtitle} · ${draft.service.duration} minutes` : "Choose the treatment or appointment reason"} open={openPicker === "service"} onClick={() => togglePicker("service")} /></div>
                      </div>
                    </div>

                    <label className="mt-2.5 block border-t border-[#e5eaf0] pt-2.5"><span className="text-[9.5px] font-bold text-[#101828]">Note</span><span className="ml-1.5 text-[8px] font-medium text-[#98a2b3]">Optional scheduling context</span><textarea value={note} onFocus={markUserInteraction} onChange={(event) => setNote(event.target.value)} placeholder="Add an internal note for the clinic…" className="mt-1 min-h-[44px] w-full resize-none rounded-[8px] border border-[#dce4ed] bg-[#fbfcfe] px-2.5 py-2 text-[9px] font-medium leading-[13px] text-[#344054] outline-none transition placeholder:text-[#a7b1bd] focus:border-[#818cf8] focus:bg-white focus:ring-2 focus:ring-[#4f46c8]/10" /></label>
                  </div>

                  <aside className="sticky top-0 flex h-full min-h-[360px] min-w-0 flex-col border-l border-[#e1e7ee] bg-[#fbfcfe] p-2.5">
                    <div><div className={`text-[17px] font-bold tracking-[-.03em] ${draft.date ? "text-[#101828]" : "text-[#a7b1bd]"}`}>{draft.date?.label ?? "Date"}</div><div className="mt-0.5 text-[8.5px] font-semibold text-[#667085]">{draft.date?.weekday ?? "Select a date"}</div><div className={`mt-2.5 border-t border-[#e1e7ee] pt-2.5 text-[10px] font-bold tabular-nums ${draft.start && draft.end ? "text-[#101828]" : "text-[#98a2b3]"}`}>{draft.start && draft.end ? `${draft.start} → ${draft.end}` : "Time pending"}</div><div className="mt-1 flex items-center gap-1 text-[8px] font-semibold text-[#667085]"><Clock3 className="h-3 w-3" />{draft.duration ? `${draft.duration} minutes` : "Duration pending"}</div></div>
                    {[["Room / branch", `${draft.room}\n${draft.branch}`], ["Patient", draft.patient?.title ?? "Without patient"], ["Assigned to", draft.doctor?.title ?? "Not assigned"], ["Group / reason", draft.service?.title ?? "Not selected"]].map(([label, value]) => <div key={label} className="mt-2.5 border-t border-[#e1e7ee] pt-2.5"><div className="text-[7.5px] font-bold uppercase tracking-[.06em] text-[#98a2b3]">{label}</div><div className={`mt-1 whitespace-pre-line text-[9px] font-bold leading-[13px] ${value.startsWith("Not") || value.startsWith("Without") ? "text-[#667085]" : "text-[#101828]"}`}>{value}</div></div>)}
                    <div className="mt-auto border-t border-[#e1e7ee] pt-2.5">
                      {appointmentSubmitted || visitPrepared ? (
                        <motion.div
                          data-appointment-result="submitted"
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="rounded-[9px] border border-[#cfd3ff] bg-[#eef2ff] px-2.5 py-2.5 text-[#3730a3] shadow-[0_6px_16px_rgba(55,48,163,.08)]"
                        >
                          <div className="flex items-center gap-1.5 text-[8.5px] font-bold"><CheckCircle2 className="h-3.5 w-3.5" />Visit created</div>
                          <div className="mt-1 text-[7.5px] font-semibold leading-[11px] text-[#5b5fa7]">Jordan Lee · Cleaning · Thu, Aug 27 · 4:45–5:45 PM</div>
                        </motion.div>
                      ) : (
                        <div className="space-y-1.5"><button type="button" onClick={() => togglePicker("start")} className="flex h-8 w-full items-center justify-center gap-1.5 rounded-[8px] border border-[#d6dee8] bg-white text-[8.5px] font-bold text-[#344054] transition hover:bg-[#f5f7fa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/25"><Search className="h-3 w-3" />Find Time</button><button type="button" onClick={() => togglePicker("date")} className="flex h-8 w-full items-center justify-center gap-1.5 rounded-[8px] border border-[#d6dee8] bg-white text-[8.5px] font-bold text-[#344054] transition hover:bg-[#f5f7fa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4f46c8]/25"><Clock3 className="h-3 w-3" />Edit Time Slot</button></div>
                      )}
                    </div>
                  </aside>
                </div>
              </div>
            </div>
            <AnimatePresence>
              {showNewPatientModal ? (
                <NewPatientModal
                  pageActive={pageActive}
                  stage={autoPatientStage}
                  firstName={autoFirstName}
                  lastName={autoLastName}
                  mobile={autoMobile}
                />
              ) : null}
            </AnimatePresence>
          </motion.div>
          ) : null}
        </AnimatePresence>
        {dismissed && !appointmentSubmitted ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#344054]/18"><button type="button" onClick={() => setDismissed(false)} className="inline-flex h-11 items-center gap-2 rounded-[11px] bg-[#101828] px-5 text-[11px] font-bold text-white shadow-[0_14px_30px_rgba(15,23,42,.24)]"><Plus className="h-4 w-4" />Open Create Visit</button></div>
        ) : null}
      </div>

      <AnimatePresence>
        <FloatingPanel key="date" open={openPicker === "date"} anchor={anchors.current.date ?? null} onClose={closePicker} width={220} ariaLabel="Choose visit date" autoFocus={false} presentation><CalendarPicker animationActive={pageActive} selected={draft.date} cursor={calendarCursor} onCursorChange={setCalendarCursor} onSelect={selectDate} autoStage={autoDateStage} readOnly /></FloatingPanel>
        <FloatingPanel key="start" open={openPicker === "start"} anchor={anchors.current.start ?? null} onClose={closePicker} width={220} ariaLabel="Choose start time" autoFocus={false} presentation><TimePicker animationActive={pageActive} title="Choose start time" options={startTimeOptions} selected={draft.start} onSelect={selectStart} autoStage={autoTimeStage} readOnly /></FloatingPanel>
        <FloatingPanel key="end" open={openPicker === "end"} anchor={anchors.current.end ?? null} onClose={closePicker} width={220} ariaLabel="Choose end time"><TimePicker animationActive={pageActive} title="Choose end time" options={endTimeOptions} selected={draft.end} onSelect={selectEnd} /></FloatingPanel>
        <FloatingPanel key="duration" open={openPicker === "duration"} anchor={anchors.current.duration ?? null} onClose={closePicker} width={188} ariaLabel="Choose visit duration"><DurationPicker selected={draft.duration} onSelect={selectDuration} /></FloatingPanel>
        <FloatingPanel key="room" open={openPicker === "room"} anchor={anchors.current.room ?? null} onClose={closePicker} width={276} ariaLabel="Choose room and branch"><RoomPicker selectedRoom={draft.room} onSelect={(option) => { updateDraft({ room: option.room, branch: option.branch }); closePicker(); }} /></FloatingPanel>
        <FloatingPanel key="patient" open={openPicker === "patient"} anchor={anchors.current.patient ?? null} onClose={closePicker} width={310} ariaLabel="Choose patient"><EntityPicker title="Select patient" searchPlaceholder="Search by patient name or mobile…" actionLabel="New patient" options={patientOptions} selectedId={draft.patient?.id} onSelect={(patient) => { updateDraft({ patient }); closePicker(); }} /></FloatingPanel>
        <FloatingPanel key="doctor" open={openPicker === "doctor"} anchor={anchors.current.doctor ?? null} onClose={closePicker} width={310} ariaLabel="Choose doctor"><EntityPicker title="Assign provider" searchPlaceholder="Search provider…" actionLabel="Add provider" options={doctorOptions} selectedId={draft.doctor?.id} onSelect={(doctor) => { updateDraft({ doctor }); closePicker(); }} /></FloatingPanel>
        <FloatingPanel key="service" open={openPicker === "service"} anchor={anchors.current.service ?? null} onClose={closePicker} width={326} ariaLabel="Choose treatment or service" autoFocus={!servicePresentation} presentation={servicePresentation}><EntityPicker title="Group / reason" searchPlaceholder="Search treatment or service…" actionLabel="Configure" options={serviceOptions} selectedId={draft.service?.id} onSelect={(service) => { const start = draft.start ?? "4:45 PM"; updateDraft({ service: service as ServiceOption, duration: (service as ServiceOption).duration, start, end: minutesToTime(timeToMinutes(start) + (service as ServiceOption).duration) }); closePicker(); }} /></FloatingPanel>
      </AnimatePresence>

    </div>
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">Read-only animated scheduling example. {liveStatus}</div>
    </>
  );
}
